/**
 * Login Page — Magic Link Authentication
 *
 * Flow:
 *   1. User enters email → POST /v2/auth/magic-link → email with secure token sent
 *   2. User clicks email link → /login/magic/:token
 *   3. Frontend calls GET /v2/auth/magic-link/:token → JWT issued via token exchange
 *   4. JWT stored, user authenticated
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, Mail, Send } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { DEVICE_PENDING_KEY } from './DeviceVerifyPage';

const API_URL = import.meta.env.VITE_API_URL || '';

type PageState = 'idle' | 'sending' | 'sent' | 'no_account' | 'error';

export default function LoginPage() {
  useDocumentTitle('Sign In');
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [pageState, setPageState] = useState<PageState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const returnTo = new URLSearchParams(location.search).get('return') || '/dashboard';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)]">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  if (authenticated) return <Navigate to={returnTo} replace />;

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setPageState('sending');
    setErrorMsg('');
    setSubmittedEmail(email.trim().toLowerCase());
    try {
      sessionStorage.setItem('login_redirect', returnTo);
      // Persist device-verify URL so MagicLinkPage can resume the device flow after auth
      if (returnTo.startsWith('/device/verify')) {
        sessionStorage.setItem(DEVICE_PENDING_KEY, returnTo);
      }
      const res = await fetch(`${API_URL}/v2/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.message || 'Failed to send sign-in link.');
        setPageState('error');
        return;
      }
      if (data.userFound === false) {
        setPageState('no_account');
        return;
      }
      setPageState('sent');
    } catch {
      setErrorMsg('Could not reach authentication server. Please try again.');
      setPageState('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[rgb(5,5,5)]">
      {/* Nav — matches marketing site */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-zinc-800/60">
        <a href="https://aitrafficlight.com" className="flex items-center gap-2">
          <TrafficLightLogo size="sm" showSub />
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
          <a href="https://aitrafficlight.com/pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="https://aitrafficlight.com/support" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Support</a>
          <a href="https://aitrafficlight.com/faq" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">FAQ</a>
          <a href="https://aitrafficlight.com/leaderboard" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Leaderboard</a>
        </nav>
        <span className="text-sm text-emerald-400 font-medium">Log In</span>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><TrafficLightLogo size="lg" showSub /></div>
          <h1 className="text-3xl font-bold text-white mb-2">AI Traffic Light for Code</h1>
          <p className="text-zinc-400">Enter your email to receive a one-click sign-in link.</p>
        </div>

        <Card className="bg-[rgb(10,10,10)] border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Sign In</CardTitle>
            <CardDescription className="text-zinc-400">
              {pageState === 'sent'
                ? 'Check your inbox for the sign-in link.'
                : "No password needed — we'll email you a magic link."}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {pageState === 'sent' && (
              <Alert className="bg-green-500/10 border-green-500/20">
                <Mail className="w-4 h-4 text-green-400" />
                <AlertDescription className="text-green-400 ml-2">
                  Sign-in link sent to <strong>{email}</strong>. Check your email and click the link
                  to log in.
                </AlertDescription>
              </Alert>
            )}

            {pageState === 'no_account' && (
              <Alert className="bg-yellow-500/10 border-yellow-500/20">
                <AlertDescription className="text-yellow-400">
                  No account found for <strong>{submittedEmail}</strong>.{' '}
                  <a href="/pricing" className="underline">
                    Start free here.
                  </a>
                </AlertDescription>
              </Alert>
            )}

            {pageState === 'error' && (
              <Alert className="bg-red-500/10 border-red-500/20">
                <AlertDescription className="text-red-400">{errorMsg}</AlertDescription>
              </Alert>
            )}

            {pageState !== 'sent' && (
              <form onSubmit={handleSendMagicLink} className="space-y-3">
                <Input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={pageState === 'sending'}
                  required
                  className="bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-400"
                />
                <Button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                  disabled={pageState === 'sending' || !email.trim()}
                >
                  {pageState === 'sending' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {pageState === 'sending' ? 'Sending…' : 'Send Magic Link'}
                </Button>
              </form>
            )}

            {pageState === 'sent' && (
              <Button
                variant="outline"
                className="w-full bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
                onClick={() => setPageState('idle')}
              >
                Use a different email
              </Button>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-zinc-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[rgb(10,10,10)] px-2 text-zinc-400">New to Code?</span>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() => (window.location.href = '/pricing')}
              className="w-full bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
            >
              Start Free
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-zinc-600 mt-6">Encrypted for your pleasure.</p>
      </div>
      </div>
    </div>
  );
}
